import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import {
  OptionItemSquare,
  OptionItemSquareList,
} from '@/pages/Onboarding/components/OptionItemSquare/OptionItemSquare';
import { useOnboarding } from '@/pages/Onboarding/context';
import { HAIR_COLOR_OPTIONS } from '@/pages/Onboarding/options/HairColor/HAIR_COLOR_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { useLayoutFormSelections } from '../../../OwnAISecond/hooks/useLayoutFormSelections';
import { OwnAiTitle, OwnAiTitleHighlight } from '../../components/Title';
import { LayoutDataType } from '../../types';

import { ScreenHairColorStyles } from './HairColor.styles';

export const ScreenHairColor: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'HairColor',
      });
    }
  }, []);

  const { ageGeneration, bodyShape } = useLayoutFormSelections();

  React.useEffect(() => {
    HAIR_COLOR_OPTIONS.forEach((option) => {
      preloadImage(
        `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai-second/${ageGeneration}/${bodyShape}/${option.id}/ai_say.jpg`,
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const hairColor = form.watch('hair_color');

  const handleSelect = (
    option: OnbOptionType<LayoutDataType['hair_color']>,
  ) => {
    form.setValue('hair_color', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        hair_color: option.title,
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  const getImgSrc = (id: string) => {
    if (webCampaign.getLayoutNotes().includes('hair-color-drawn')) {
      // TODO upload images to cloudfront
      return `../assets/options/hair_color_drawn/${id}.png`;
    }

    return `https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/hair_color/${id}.png`;
  };

  return (
    <ScreenHairColorStyles>
      <OnboardingNavigation />
      <OwnAiTitle>
        Select her <OwnAiTitleHighlight>hair color</OwnAiTitleHighlight>
      </OwnAiTitle>

      <IonContent>
        <OptionItemSquareList>
          {HAIR_COLOR_OPTIONS.map((option) => (
            <OptionItemSquare
              key={option.id}
              isSelected={hairColor === option.id}
              onClick={() => handleSelect(option)}
              bgSrc={getImgSrc(option.id)}
              title={option.title}
              TEST_ID={`hair_color-${option.id}`}
            />
          ))}
        </OptionItemSquareList>
      </IonContent>
    </ScreenHairColorStyles>
  );
};
